import * as THREE from 'three';
import { WallType, WallConnectionType } from '../types/floorplannerTypes';
import { WallUpdateManager, SpatialGridCell } from '../types/wallCacheTypes';
import { WallChangeDetector } from './wallChangeDetection';
import { jointDetector } from './JointDetector';

export class WallDependencyGraph {
  private dependencyGraph: Map<string, Set<string>> = new Map();
  private spatialIndex: Map<string, Set<string>> = new Map();
  private gridSize: number = 10; // Grid size for spatial indexing
  private wallBoundingBoxes: Map<string, THREE.Box2> = new Map();

  /**
   * Build or update the dependency graph for all walls
   */
  buildDependencyGraph(walls: Map<string, WallType>): void {
    // Invalidate joint detector cache at the start to ensure fresh snapshot
    jointDetector.invalidateCache();

    this.dependencyGraph.clear();
    this.spatialIndex.clear();
    this.wallBoundingBoxes.clear();

    // Build spatial index first
    this.buildSpatialIndex(walls);

    // Build dependency graph
    walls.forEach((wall, wallId) => {
      this.updateWallDependencies(wallId, wall, walls);
    });
  }

  /**
   * Build spatial index for fast proximity queries
   */
  private buildSpatialIndex(walls: Map<string, WallType>): void {
    walls.forEach((wall, wallId) => {
      const bounds = this.getWallBounds(wall);
      this.wallBoundingBoxes.set(wallId, bounds);

      // Add wall to spatial grid cells it occupies
      const cells = this.getGridCellsForBounds(bounds);
      cells.forEach(cellKey => {
        if (!this.spatialIndex.has(cellKey)) {
          this.spatialIndex.set(cellKey, new Set());
        }
        this.spatialIndex.get(cellKey)!.add(wallId);
      });
    });
  }

  /**
   * Update dependencies for a specific wall
   */
  updateWallDependencies(wallId: string, wall: WallType, allWalls: Map<string, WallType>): void {
    const dependencies = new Set<string>();

    // Only explicit topology should expand wall updates. Proximity-based
    // dependencies made a dense floorplan behave like one transitive component,
    // so a local connect/disconnect could recompute most of the plan.
    wall.centerConnections?.forEach(connection => {
      dependencies.add(connection.id);
    });

    wall.edgeConnections?.forEach(connection => {
      if (connection.targetWallId !== wallId) {
        dependencies.add(connection.targetWallId);
      }
    });

    // Add joint-based dependencies
    const joints = jointDetector.getJointsForWall(wallId);
    joints.forEach(joint => {
      joint.connectedWalls.forEach(wallConn => {
        if (wallConn.wallId !== wallId) {
          dependencies.add(wallConn.wallId);
        }
      });
    });

    this.dependencyGraph.set(wallId, dependencies);
  }

  /**
   * Get walls that might be affected by changes to a specific wall
   */
  getAffectedWalls(wallId: string): string[] {
    const affected = new Set<string>();
    affected.add(wallId);

    this.dependencyGraph.get(wallId)?.forEach(depWallId => {
      affected.add(depWallId);
    });

    this.dependencyGraph.forEach((deps, depWallId) => {
      if (deps.has(wallId)) {
        affected.add(depWallId);
      }
    });

    return Array.from(affected);
  }

  /**
   * Get only directly connected walls (for drag operations)
   * This is much more targeted and doesn't cause cascading updates
   */
  getDirectlyConnectedWalls(wallId: string): string[] {
    const wall = this.dependencyGraph.get(wallId);
    if (!wall) return [wallId]; // Return the wall itself if no dependencies found

    const directlyConnected = new Set<string>();
    directlyConnected.add(wallId); // Always include the wall itself

    // Only add walls that are directly connected (not transitive)
    wall.forEach(connectedWallId => {
      directlyConnected.add(connectedWallId);
    });

    return Array.from(directlyConnected);
  }

  /**
   * Get walls that share a joint with the given wall
   */
  getJointConnectedWalls(wallId: string): string[] {
    const jointConnectedWalls = new Set<string>();
    const joints = jointDetector.getJointsForWall(wallId);

    joints.forEach(joint => {
      joint.connectedWalls.forEach(connectedWall => {
        if (connectedWall.wallId !== wallId) {
          jointConnectedWalls.add(connectedWall.wallId);
        }
      });
    });

    return Array.from(jointConnectedWalls);
  }

  /**
   * Get walls in proximity to a specific wall
   */
  getWallsInProximity(wallId: string, allWalls: Map<string, WallType>, radius: number = 20): string[] {
    const wall = allWalls.get(wallId);
    if (!wall) return [];

    const bounds = this.wallBoundingBoxes.get(wallId);
    if (!bounds) return [];

    // Expand bounds by radius
    const expandedBounds = new THREE.Box2();
    expandedBounds.min.copy(bounds.min).addScalar(-radius);
    expandedBounds.max.copy(bounds.max).addScalar(radius);

    const cells = this.getGridCellsForBounds(expandedBounds);
    const wallsInProximity = new Set<string>();

    cells.forEach(cellKey => {
      const cellWalls = this.spatialIndex.get(cellKey);
      if (cellWalls) {
        cellWalls.forEach(wallIdInCell => {
          if (wallIdInCell !== wallId) {
            wallsInProximity.add(wallIdInCell);
          }
        });
      }
    });

    return Array.from(wallsInProximity);
  }

  /**
   * Get bounding box for a wall
   */
  private getWallBounds(wall: WallType): THREE.Box2 {
    const bounds = new THREE.Box2();
    bounds.setFromPoints([wall.start, wall.end]);

    // Expand by wall width to account for wall thickness
    const halfWidth = (wall.wallWidth || 0) / 2;
    const expansion = new THREE.Vector2(halfWidth, halfWidth);
    bounds.min.sub(expansion);
    bounds.max.add(expansion);

    return bounds;
  }

  /**
   * Get grid cells that bounds intersect with
   */
  private getGridCellsForBounds(bounds: THREE.Box2): string[] {
    const cells = new Set<string>();

    const minX = Math.floor(bounds.min.x / this.gridSize);
    const maxX = Math.ceil(bounds.max.x / this.gridSize);
    const minY = Math.floor(bounds.min.y / this.gridSize);
    const maxY = Math.ceil(bounds.max.y / this.gridSize);

    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        cells.add(`${x},${y}`);
      }
    }

    return Array.from(cells);
  }

  /**
   * Get grid cell key for a point
   */
  getGridCellForPoint(point: THREE.Vector2): string {
    const x = Math.floor(point.x / this.gridSize);
    const y = Math.floor(point.y / this.gridSize);
    return `${x},${y}`;
  }

  /**
   * Check if two walls might intersect based on spatial proximity
   */
  wallsMightIntersect(wall1Id: string, wall2Id: string, allWalls: Map<string, WallType>): boolean {
    const bounds1 = this.wallBoundingBoxes.get(wall1Id);
    const bounds2 = this.wallBoundingBoxes.get(wall2Id);

    if (!bounds1 || !bounds2) return false;

    return bounds1.intersectsBox(bounds2);
  }

  /**
   * Remove a wall from the dependency graph
   */
  removeWall(wallId: string): void {
    this.dependencyGraph.delete(wallId);

    // Remove from spatial index
    this.wallBoundingBoxes.delete(wallId);
    this.spatialIndex.forEach((walls, cellKey) => {
      walls.delete(wallId);
      if (walls.size === 0) {
        this.spatialIndex.delete(cellKey);
      }
    });

    // Remove from other walls' dependencies
    this.dependencyGraph.forEach((deps, wallId) => {
      deps.delete(wallId);
    });
  }

  /**
   * Get statistics about the dependency graph
   */
  getStats(): {
    totalWalls: number;
    totalDependencies: number;
    averageDependencies: number;
    spatialCells: number;
  } {
    let totalDependencies = 0;
    this.dependencyGraph.forEach(deps => {
      totalDependencies += deps.size;
    });

    return {
      totalWalls: this.dependencyGraph.size,
      totalDependencies,
      averageDependencies: this.dependencyGraph.size > 0 ? totalDependencies / this.dependencyGraph.size : 0,
      spatialCells: this.spatialIndex.size
    };
  }

  /**
   * Visualize dependency graph (for debugging)
   */
  visualizeDependencies(): { wallId: string; dependencies: string[] }[] {
    const result: { wallId: string; dependencies: string[] }[] = [];

    this.dependencyGraph.forEach((deps, wallId) => {
      result.push({
        wallId,
        dependencies: Array.from(deps)
      });
    });

    return result;
  }

  /**
   * Update dependencies based on joint information
   */
  updateJointBasedDependencies(wallId: string, allWalls: Map<string, WallType>): void {
    const joints = jointDetector.getJointsForWall(wallId);
    const dependencies = this.dependencyGraph.get(wallId) || new Set();

    // Add all walls that share joints with this wall
    joints.forEach(joint => {
      joint.connectedWalls.forEach(wallConn => {
        if (wallConn.wallId !== wallId) {
          dependencies.add(wallConn.wallId);
        }
      });
    });

    this.dependencyGraph.set(wallId, dependencies);
  }


  /**
   * Check if two walls share any joints
   */
  wallsShareJoint(wall1Id: string, wall2Id: string): boolean {
    const wall1Joints = jointDetector.getJointsForWall(wall1Id);
    const wall2Joints = jointDetector.getJointsForWall(wall2Id);

    return wall1Joints.some(joint1 =>
      wall2Joints.some(joint2 => joint1.id === joint2.id)
    );
  }

  /**
   * Get enhanced statistics including joint information
   */
  getEnhancedStats(): {
    totalWalls: number;
    totalDependencies: number;
    averageDependencies: number;
    spatialCells: number;
    jointBasedDependencies: number;
    wallsWithJoints: number;
  } {
    const basicStats = this.getStats();

    let jointBasedDependencies = 0;
    let wallsWithJoints = 0;

    this.dependencyGraph.forEach((deps, wallId) => {
      const joints = jointDetector.getJointsForWall(wallId);
      if (joints.length > 0) {
        wallsWithJoints++;
        // Count dependencies that come from joints
        joints.forEach(joint => {
          jointBasedDependencies += joint.connectedWalls.length - 1; // Exclude self
        });
      }
    });

    return {
      ...basicStats,
      jointBasedDependencies,
      wallsWithJoints
    };
  }

  /**
   * Clear all data
   */
  clear(): void {
    this.dependencyGraph.clear();
    this.spatialIndex.clear();
    this.wallBoundingBoxes.clear();
  }
}
